import React, { useState, useCallback } from 'react';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import GiftIcon from '@src/assets/icons/gift.svg';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { NEW_ACTIONS_HEADER_MENU } from '@src/constants';
import Tooltip from '@src/components/Tooltip';
import { useTranslation } from 'react-i18next';
import CreditIntro from '@src/containers/CreditIntro';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useSelector } from 'react-redux';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useCheckEvent from '@src/hooks/useEvent';
import NotificationPanel from '@src/components/NotificationPanel';
import EventHeaderButton from '@src/containers/TopPaidEvent/EventHeaderButton';

const BUTTON_STYLES = {
  base: {
    height: '40px',
    minWidth: '40px',
    borderRadius: '12px',
    padding: '0px',
  },
  outline: {
    border: '1px solid #1E164E29',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
};

const NewActionsHeader = ({
  menuShow = [],
  onTaskCredits,
  onLeaderboardVoice,
  margin = '0px 8px',
}) => {
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const hasNewNotification = useSelector(
    (state) => state.notification.hasNewNotification,
  );

  const creditIntroConfig = getFeatureValue(FEATURE_KEYS.CREDITS_INTRO, {
    userId: user.id,
    email: user.email,
  });
  const creditIntroEnabled =
    creditIntroConfig !== undefined &&
    Object.hasOwn(creditIntroConfig, 'releaseDate');

  const referralConfig = useSelector((state) => state.referralConfig);
  const { referralBanner } = referralConfig || {};
  const hasReferralBanner = Object.keys(referralBanner).length > 0;

  const eventInvalid = useCheckEvent();

  const notificationEnabled = getFeatureValue(FEATURE_KEYS.NOTIFICATION, {
    userId: user.id,
    email: user.email,
  });

  const handleOpenPanel = useCallback((e) => {
    setNotificationAnchorEl(e.currentTarget);
  }, []);

  const handleClosePanel = useCallback(() => {
    setNotificationAnchorEl(null);
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginRight: '8px',
        margin,
      }}
    >
      <EventHeaderButton />
      {menuShow.includes(NEW_ACTIONS_HEADER_MENU.LEADERBOARD_VOICE) &&
        !eventInvalid &&
        hasReferralBanner && (
          <Tooltip arrow title={t('leaderBoardVoice')} placement="bottom">
            <div>
              <CommonButton
                variant="outlineSmall"
                sx={{
                  ...BUTTON_STYLES.base,
                  ...BUTTON_STYLES.outline,
                }}
                onClick={onLeaderboardVoice}
              >
                <EmojiEventsRoundedIcon
                  sx={{ fontSize: '24px', color: COLOR.navyBlue }}
                />
              </CommonButton>
            </div>
          </Tooltip>
        )}
      {menuShow.includes(NEW_ACTIONS_HEADER_MENU.TASK_CREDIT) && (
        <Tooltip arrow title={t('taskCredits')} placement="bottom">
          <div>
            <CommonButton
              variant="outlineSmall"
              sx={{
                ...BUTTON_STYLES.base,
                ...BUTTON_STYLES.outline,
              }}
              onClick={onTaskCredits}
            >
              <img
                src={GiftIcon}
                alt="gift"
                style={{ width: '24px', height: '24px' }}
              />
            </CommonButton>
          </div>
        </Tooltip>
      )}
      {menuShow.includes(NEW_ACTIONS_HEADER_MENU.NOTIFICATION) &&
        notificationEnabled && (
          <>
            <Tooltip arrow title={t('notifications')} placement="bottom">
              <div style={{ position: 'relative' }}>
                <CommonButton
                  variant="outlineSmall"
                  sx={{ ...BUTTON_STYLES.base, ...BUTTON_STYLES.outline }}
                  onClick={handleOpenPanel}
                >
                  <NotificationsRoundedIcon
                    sx={{ fontSize: '24px', color: COLOR.navyBlue }}
                  />
                </CommonButton>
                {hasNewNotification && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '9px',
                      right: '7px',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: COLOR.functionalRed.default,
                      border: '1.5px solid white',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </Tooltip>
            <NotificationPanel
              anchorEl={notificationAnchorEl}
              onClose={handleClosePanel}
            />
          </>
        )}
      {menuShow.includes(NEW_ACTIONS_HEADER_MENU.GUIDE_CREDIT) &&
        creditIntroEnabled && <CreditIntro />}
    </div>
  );
};

export default NewActionsHeader;
